import {
  Button,
  Card,
  DateFormats,
  DescriptionList,
  type DescriptionListItem,
  formatDateWithTimeAndTimezone,
  formatISODate,
  formatNumber,
  Inline,
  Modal,
  type ModalProps,
  Notification,
  notifySuccess,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useCompensationConfiguration,
  useCreateCompensationVariantPaymentMutation,
  useEmployee,
} from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  CompensationConfigRecurrence,
  CompensationConfigTypes,
  CompensationSource,
  type CompensationVariantPaymentPrefunding,
} from '@omnipresentgroup/types';
import { getCountryCode } from 'countries-list';
import { useEffect, useMemo, useState } from 'react';

import { BonusDisclaimerCheckbox } from '../../components';
import type { AddVariantPaymentFormFields } from '../types';

type Props = {
  modalProps: Omit<ModalProps, 'children'>;
  handleCancel: () => void;
  handleSuccess: () => void;
  employeeId: string;
  formValues: AddVariantPaymentFormFields;
  prefunding?: CompensationVariantPaymentPrefunding;
};

const DetailCard = ({
  id,
  label,
  value = '-',
}: {
  id: string;
  label: string;
  value?: string;
}) => {
  return (
    <Card px="32" py="16">
      <Stack gap="8">
        <Typography as="label" size="12" weight="medium" color="subtle" id={id}>
          {label}
        </Typography>
        <Typography aria-labelledby={id} as="span" size="14">
          {value}
        </Typography>
      </Stack>
    </Card>
  );
};

export const AddVariantPaymentReviewModal = ({
  modalProps,
  handleCancel,
  handleSuccess,
  employeeId,
  formValues,
  prefunding,
}: Props) => {
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: employeeId,
    enabled: Boolean(employeeId),
  });

  const { mutate: createVariantPayment, isPending: isCreatingVariantPayment } =
    useCreateCompensationVariantPaymentMutation({
      employeeId,
      onSuccess: (_data, variables) => {
        if (!variables.dryRun) {
          notifySuccess({
            title: `${toSentenceCase(
              formValues.type!,
            )} has been submitted successfully`,
          });
          handleSuccess?.();
        }
      },
    });

  const employeeName = useMemo(() => {
    return `${employee?.personalDetails?.firstName} ${employee?.personalDetails?.lastName}`;
  }, [
    employee?.personalDetails?.firstName,
    employee?.personalDetails?.lastName,
  ]);

  const employeeCountryCode = useMemo(
    () =>
      ((employee?.country?.name && getCountryCode(employee?.country?.name)) ||
        '') as string,
    [employee?.country?.name],
  );

  const { data: compensationConfig } = useCompensationConfiguration({
    countryCode: employeeCountryCode,
    source: CompensationSource.MONTH_ON_MONTH,
    enabled: Boolean(employeeCountryCode),
  });

  const variantPaymentName = useMemo(
    () =>
      compensationConfig?.variantPaymentsTypes?.find(
        (item) =>
          item.type === formValues.type && item.subtype === formValues.subtype,
      )?.name,
    [
      compensationConfig?.variantPaymentsTypes,
      formValues.subtype,
      formValues.type,
    ],
  );

  const isRecurring = useMemo(
    () =>
      formValues?.recurrence &&
      formValues?.recurrence !== CompensationConfigRecurrence.ONE_OFF,
    [formValues?.recurrence],
  );

  const list: DescriptionListItem[] = useMemo(
    () => [
      {
        header: 'Type',
        detail: variantPaymentName || '-',
      },
      {
        header: 'Gross amount',
        detail: formatNumber({
          input: Number(formValues.amount?.amount || 0),
          currency: formValues.amount?.currency,
        }),
      },
      {
        header: 'Recurrence',
        detail: formValues.recurrence
          ? toSentenceCase(formValues.recurrence)
          : '-',
      },
      {
        header: `${isRecurring ? 'Starting' : 'Effective'} month`,
        detail: formValues.effectiveDate
          ? formatISODate(formValues.effectiveDate, DateFormats.DateWithoutDay)
          : '-',
      },
      ...(isRecurring
        ? [
            {
              header: 'Ending month',
              detail: formValues.untilEndOfContract
                ? 'Contract end'
                : // eslint-disable-next-line unicorn/no-nested-ternary
                  formValues.endDate
                  ? formatISODate(
                      formValues.endDate,
                      DateFormats.DateWithoutDay,
                    )
                  : '-',
            },
          ]
        : []),
    ],
    [
      formValues.amount?.amount,
      formValues.amount?.currency,
      formValues.effectiveDate,
      formValues.endDate,
      formValues.recurrence,
      formValues.untilEndOfContract,
      isRecurring,
      variantPaymentName,
    ],
  );

  // Reset checkbox when values change
  useEffect(() => {
    setDisclaimerAccepted(false);
  }, [formValues]);

  const PrefundingNotification = () => {
    return prefunding?.required ? (
      <Notification
        id="prefunding-notice"
        title={`You've either submitted a single ${toSentenceCase(
          formValues.type!,
        ).toLowerCase()} that exceeds £10,000 (equivalent) OR cumulatively across your employees on OmniPlatform the variable pay in this payroll cycle exceeds £30,000 (equivalent).`}
        description={`The cutoff date for submission is the ${formatDateWithTimeAndTimezone(
          prefunding.variantPaymentCutoffDate!,
        )}. After submission, please pay the invoice by ${formatDateWithTimeAndTimezone(
          prefunding.paymentDueDate!,
        )} for it to be paid in that month’s payroll. `}
        link={{
          text: 'Pre-funding policy',
          to: 'https://help.omnipresent.com/en/articles/10261307-new-policy-announcement-pre-funding-for-large-bonuses',
        }}
      />
    ) : (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <></>
    );
  };

  return (
    <Modal {...modalProps} span={6}>
      <Modal.Header
        title={`Confirm ${toSentenceCase(formValues.type || '')}`}
        description={employeeName}
      />
      <Modal.Body>
        <Stack>
          <PrefundingNotification />
          <DescriptionList id="add-variant-payment-review-fields" list={list} />
          <DetailCard id="reason" label="Reason" value={formValues.reason} />
          <DetailCard
            id="additionalNotes"
            label="Additional notes"
            value={formValues.additionalNotes}
          />
          {formValues?.type === CompensationConfigTypes.BONUS && (
            <BonusDisclaimerCheckbox
              disclaimerAccepted={disclaimerAccepted}
              setDisclaimerAccepted={setDisclaimerAccepted}
            />
          )}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isCreatingVariantPayment}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              createVariantPayment({
                employeeId,
                source: CompensationSource.MONTH_ON_MONTH,
                type: formValues.type!,
                subtype: formValues.subtype!,
                amount: {
                  amount: formValues.amount?.amount!,
                  currency: formValues.amount?.currency!,
                },
                recurrence: formValues.recurrence!,
                effectiveDate: formValues.effectiveDate!,
                endDate: formValues.endDate,
                reason: formValues.reason!,
                additionalNotes: formValues.additionalNotes,
              })
            }
            disabled={
              isLoadingEmployee ||
              (formValues?.type === CompensationConfigTypes.BONUS &&
                !disclaimerAccepted)
            }
            loading={isCreatingVariantPayment}
          >
            Submit
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
